import React, { useEffect, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFoodItems,
  deleteFoodItem,
  fetchFoodItemsSearch,
} from "../../redux/actions/allPostsActions";
import { RootState, AppDispatch } from "../../redux/store";
import DeleteModal from "../../components/Deletemodal/DeleteModal";
import { FoodItem } from "../../redux/slices/allPostsSlice";
import { LuTrash } from "react-icons/lu";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 6;

const AllPosts: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { foodItems, loading, totalElements } = useSelector(
    (state: RootState) => state.allPosts
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [foodItemToDelete, setFoodItemToDelete] = useState<FoodItem | null>(
    null
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (searchQuery.trim()) {
      dispatch(fetchFoodItemsSearch({ query:searchQuery}));
    } else {
      dispatch(fetchFoodItems({ page: currentPage - 1, size: PAGE_SIZE }));
    }
  }, [dispatch, searchQuery,currentPage]);
  
  const totalPages = Math.ceil(totalElements / PAGE_SIZE);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);

    if (value === "") {
      setCurrentPage(1);
    }
  };

  const openDeleteModal = (item: FoodItem) => {
    setFoodItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setFoodItemToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    if (foodItemToDelete) {
      await dispatch(deleteFoodItem(foodItemToDelete.id));
      closeDeleteModal();
      dispatch(fetchFoodItems({ page: currentPage - 1, size: PAGE_SIZE }));
    }
  };

  return (
    <div className="flex w-full">
      <div className="flex flex-col items-center flex-grow bg-white">
        <div className="w-full">
          <div className="pt-8">
            <h1 className="text-2xl font-medium">{t("allposts.title")}</h1>
            <p className="text-sm">{t("allposts.description")}</p>
          </div>
          <div className="flex justify-between mt-4">
            <div className="flex items-end">
              <h1 className="font-medium">{t("allposts.list")}</h1>
            </div>
            <div className="relative w-[320px]">
              <input
                type="search"
                className="w-full h-[40px] border-2 rounded-lg pl-9 text-sm pr-2 outline-none"
                placeholder={t("allposts.searchPlaceholder")}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <CiSearch
                className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
                size={16}
              />
            </div>
          </div>
  
          <div className="mt-6">
            <table className="w-full table-auto">
              <thead>
                <tr className="h-[40px] border-b w-full">
                  <th className="pl-6 pr-3 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("allposts.image")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("allposts.foodName")}
                  </th>
                  <th className="pl-6 pr-3 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("allposts.restaurantName")}
                  </th>
                  <th className="px-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("allposts.price")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("allposts.category")}
                  </th>
                  <th className="pl-6 pr-3 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("allposts.contactNumber")}
                  </th>
                  <th className="pl-6 text-sm font-medium text-left text-gray-500 border-r-2">
                    {t("allposts.location")}
                  </th>
                  <th className="w-[136px]"></th>
                </tr>
              </thead>
              <tbody className="relative">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12">
                      <div className="flex justify-center items-center w-full h-full">
                        <Spin size="large" />
                      </div>
                    </td>
                  </tr>
                ) : (
                  foodItems.map((item: FoodItem, index) => (
                    <tr
                      key={item.id}
                      className={`h-[56px] border-b ${
                        index % 2 === 0 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        <img
                          className="h-[36px] w-[43px]"
                          alt="food"
                          src={item.imageUrl}
                        />
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {item.foodName}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {item.restaurantName}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {item.price}$
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {item.categories.join(", ")}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {item.contactNumber}
                      </td>
                      <td className="pl-6 text-sm font-normal text-left border-r-2">
                        {item.location}
                      </td>
                      <td className="w-[136px]">
                        <div className="flex justify-center gap-2">
                          <button
                            className="border w-[40px] h-[40px] flex justify-center items-center rounded-lg border-gray-200 hover:bg-gray-50 bg-white"
                            onClick={() => openDeleteModal(item)}
                          >
                            <LuTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
  
          <div className="flex gap-2 mt-4 justify-left">
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className="border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF] w-[40px] h-[40px] flex justify-center items-center"
            >
              <IoIosArrowBack size={10} />
            </button>
  
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => handlePageChange(index + 1)}
                className={`w-[40px] h-[40px] flex justify-center items-center border rounded-lg text-sm ${
                  currentPage === index + 1 ? "border-black" : "text-gray-700"
                }`}
              >
                {index + 1}
              </button>
            ))}
  
            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className="w-[40px] h-[40px] flex justify-center items-center border rounded-lg disabled:opacity-50 disabled:bg-[#E9ECEF]"
            >
              <IoIosArrowForward size={10} />
            </button>
          </div>
        </div>
      </div>
  
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        categoryName={foodItemToDelete ? foodItemToDelete.foodName : ""}
      />
    </div>
  );
};

export default AllPosts;
